import { useCallback, useMemo } from 'react';
import { isString } from 'lodash';
import type { Column, Filters, SortInfo } from '../types';
import { FILTER_EMPTY_KEY, FILTER_NULL_KEY } from '../DataGrid';

const RENDER_BACTCH_SIZE = 8;

interface ViewportRowsArgs<R, SR> {
  rawRows: readonly R[];
  rowHeight: number;
  clientHeight: number;
  scrollTop: number;
  enableVirtualization: boolean;
  rawColumns: readonly Column<R, SR>[];
  sortInfo: SortInfo;
  rowFilters: Filters;
  searchKey?: string;
  sortRows: (rows: R[]) => R[];
}

export function useViewportRows<R, SR>({
  rawRows,
  rawColumns,
  rowHeight,
  clientHeight,
  scrollTop,
  enableVirtualization,
  sortInfo,
  rowFilters,
  searchKey,
  sortRows,
}: ViewportRowsArgs<R, SR>) {
  const rowsCount = rawRows?.length || 0;

  const filterRows = useCallback(
    (rawRows: readonly R[]): R[] => {
      if (!rawRows || rawRows.length === 0) {
        return [];
      }

      const filterColumnKeys = Object.keys(rowFilters).filter(
        (columnKey) => rowFilters[columnKey]?.size,
      );
      if (!filterColumnKeys.length && !searchKey) {
        return [...rawRows];
      }
      return rawRows.filter((row: R) => {
        let isPass = true;
        if (searchKey) {
          /**
           * 查找过滤逻辑
           */
          let hit = false;
          for (const columnName of Object.keys(row as Record<string, unknown>)) {
            const columnValue = (row as Record<string, unknown>)[columnName] || '';
            if (
              isString(columnValue) &&
              columnValue.toLowerCase().indexOf(searchKey?.toLowerCase()) > -1
            ) {
              hit = true;
              break;
            }
          }
          if (!hit) {
            return false;
          }
        }
        for (const columnKey of filterColumnKeys) {
          let cellValue = (row as Record<string, unknown>)[columnKey];
          if (cellValue === null) {
            /**
             * 把null当作字符串来处理，从而支持搜索和筛选
             */
            cellValue = FILTER_NULL_KEY;
          } else if (typeof cellValue === 'string' && !cellValue.trim()) {
            cellValue = FILTER_EMPTY_KEY;
          }
          if (!rowFilters[columnKey]?.has(cellValue)) {
            isPass = false;
            break;
          }
        }
        return isPass;
      });
    },
    [rowFilters, searchKey, rawColumns],
  );

  const rows = useMemo(() => {
    return sortRows(filterRows(rawRows));
  }, [rawRows, searchKey, sortInfo, rowFilters, sortRows]);

  if (!enableVirtualization) {
    return {
      rowOverscanStartIdx: 0,
      rowOverscanEndIdx: rows.length - 1,
      rows,
      rowsCount,
    };
  }

  const overscanThreshold = 10;
  const rowVisibleStartIdx = Math.floor(scrollTop / rowHeight);
  const rowVisibleEndIdx = Math.min(
    rows.length - 1,
    Math.floor((scrollTop + clientHeight) / rowHeight),
  );
  const rowOverscanStartIdx = Math.max(
    0,
    Math.floor((rowVisibleStartIdx - overscanThreshold) / RENDER_BACTCH_SIZE) * RENDER_BACTCH_SIZE,
  );
  const rowOverscanEndIdx = Math.min(
    rows.length - 1,
    Math.ceil((rowVisibleEndIdx + overscanThreshold) / RENDER_BACTCH_SIZE) * RENDER_BACTCH_SIZE,
  );

  return {
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    rowVisibleStartIdx,
    rowVisibleEndIdx,
    rows,
    rowsCount,
  };
}
